export const DRAW_KINDS = ['stroke', 'line', 'rect', 'ellipse', 'text'];
export const DRAW_WIDTHS = [2, 4, 8];
export const TEXT_SIZES = [16, 24, 36];
/** Whether a viewer is sent a drawing: everyone, except a planning drawing, which is the DM's alone. */
export const seesDrawing = (d, viewer) => !d.planning || viewer.role === 'dm';
/**
 * `strokePoints`: a stroke's points (pairs) at most — 2,000 pairs is some 20 KB on the wire, far under the socket's
 * 1 MB frame. `drawings`: per scene. `min`/`max`: a sane board-pixel bound; the client keeps a mark within one square
 * of the map.
 */
export const DRAW_LIMITS = { strokePoints: 2000, drawings: 300, text: 200, min: -20_000, max: 60_000 };
/** Said when a scene holds as many as it can (design/28 §1). */
export const DRAWINGS_FULL = 'This map has as many drawings as it can hold. Erase some first.';
export const DRAW_EVENT_KINDS = ['draw.add', 'draw.addMany', 'draw.erase', 'draw.eraseMine'];
/** The most drawings one `draw.addMany` carries: a stamp's marks at most (design/28 §4a.6). */
export const ADD_MANY_MAX = 50;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const COLOUR_RE = /^#[0-9a-f]{6}$/i;
// eslint-disable-next-line no-control-regex
const CONTROL_RE = /[\u0000-\u001f\u007f]/;
const pointsFor = (kind, n) => {
    if (kind === 'text')
        return n === 1;
    if (kind === 'stroke')
        return n >= 2 && n <= DRAW_LIMITS.strokePoints;
    return n === 2;
};
/**
 * `bounds`: the range a point may take. A drawing on a map is in `DRAW_LIMITS`; a stamp's marks (issue 1034) are
 * relative to the stamp's centre and may be negative by as much as a map is wide.
 */
export function parseDrawing(v, bounds = DRAW_LIMITS) {
    if (typeof v !== 'object' || v === null)
        return null;
    const d = v;
    if (!isUuid(d.id))
        return null;
    if (d.ownerId !== undefined && d.ownerId !== '' && !isUuid(d.ownerId))
        return null;
    if (!DRAW_KINDS.includes(d.kind))
        return null;
    const kind = d.kind;
    if (typeof d.colour !== 'string' || !COLOUR_RE.test(d.colour))
        return null;
    if (!DRAW_WIDTHS.includes(d.width))
        return null;
    if (!Array.isArray(d.points) || d.points.length % 2 !== 0 || !pointsFor(kind, d.points.length / 2))
        return null;
    const points = [];
    for (const p of d.points) {
        if (typeof p !== 'number' || !Number.isFinite(p) || p < bounds.min || p > bounds.max)
            return null;
        points.push(Math.round(p * 2) / 2);
    }
    if (d.planning !== undefined && d.planning !== true && d.planning !== false)
        return null;
    const out = { id: d.id, ownerId: typeof d.ownerId === 'string' ? d.ownerId : '', kind, points, colour: d.colour.toLowerCase(), width: d.width };
    if (d.planning === true)
        out.planning = true;
    if (kind !== 'text')
        return d.text === undefined && d.size === undefined ? out : null;
    if (typeof d.text !== 'string' || CONTROL_RE.test(d.text))
        return null;
    const text = d.text.trim();
    if (text.length < 1 || text.length > DRAW_LIMITS.text)
        return null;
    if (!TEXT_SIZES.includes(d.size))
        return null;
    return { ...out, text, size: d.size };
}
export function parseDrawBody(b) {
    switch (b.kind) {
        case 'draw.add': {
            const drawing = parseDrawing(b.drawing);
            return drawing ? { kind: 'draw.add', drawing } : null;
        }
        case 'draw.addMany': {
            if (!Array.isArray(b.drawings) || b.drawings.length < 1 || b.drawings.length > ADD_MANY_MAX)
                return null;
            const drawings = b.drawings.map((d) => parseDrawing(d));
            if (!drawings.every((d) => d !== null))
                return null;
            return new Set(drawings.map((d) => d.id)).size === drawings.length ? { kind: 'draw.addMany', drawings } : null;
        }
        case 'draw.erase': return isUuid(b.id) ? { kind: 'draw.erase', id: b.id } : null;
        case 'draw.eraseMine': {
            if (b.ids === undefined)
                return { kind: 'draw.eraseMine', ids: [] };
            if (!Array.isArray(b.ids) || b.ids.length > DRAW_LIMITS.drawings || !b.ids.every(isUuid))
                return null;
            return { kind: 'draw.eraseMine', ids: [...b.ids] };
        }
        default: return null;
    }
}
/** A snapshot's drawings, each read as the wire's; anything unreadable is dropped. */
export function normaliseDrawings(v) {
    if (!Array.isArray(v))
        return [];
    return v.map((d) => parseDrawing(d)).filter((d) => d !== null);
}
export function reduceDrawings(drawings, body) {
    switch (body.kind) {
        case 'draw.add': return drawings.some((d) => d.id === body.drawing.id) ? drawings : [...drawings, { ...body.drawing, points: [...body.drawing.points] }];
        case 'draw.addMany': {
            const have = new Set(drawings.map((d) => d.id));
            const fresh = body.drawings.filter((d) => !have.has(d.id));
            return fresh.length ? [...drawings, ...fresh.map((d) => ({ ...d, points: [...d.points] }))] : drawings;
        }
        case 'draw.erase': return drawings.some((d) => d.id === body.id) ? drawings.filter((d) => d.id !== body.id) : drawings;
        case 'draw.eraseMine': {
            const gone = new Set(body.ids);
            return drawings.some((d) => gone.has(d.id)) ? drawings.filter((d) => !gone.has(d.id)) : drawings;
        }
        default: return drawings;
    }
}
/** A departed person's drawings stay on the map, belonging to nobody (design/28 §2.3). */
export function pruneDrawings(drawings, gone) {
    return drawings.some((d) => gone.has(d.ownerId)) ? drawings.map((d) => (gone.has(d.ownerId) ? { ...d, ownerId: '' } : d)) : drawings;
}
/** Whether a person may erase a drawing: its author; the DM one whose author has left. Nobody else, the DM included. */
export const mayEraseDrawing = (d, viewer) => (d.ownerId === '' ? viewer.role === 'dm' : d.ownerId === viewer.id);
/** The refusal a person is told when the drawing is somebody else's (design/28 §2.1). `handle` with its '@', or ''. */
export const notYourDrawing = (d, handle) => (d.ownerId === ''
    ? 'Only the DM can erase a drawing whose author has left the table.'
    : `That drawing is ${handle || 'someone else\'s'}${handle ? '\'s' : ''}; only they can erase it.`);
//# sourceMappingURL=drawings.js.map